<template>
  <config-base class="opc-zcm-config" @view-details="showPlanDetailDialog">
    <div slot="config">
      <div>
        <span class="opc-zcm-config__item-label">{{ $t('billing.config.select_number_license') }}</span>
        <zm-tooltip aria-label="Learn more about select the number of users">
          <span slot="content" class="opc-zcm-config__tips-content">
            {{ $t('billing.zcm.select_license_tip') }}
          </span>
          <i class="zm-icon-info-outline opc-zcm-config__info-icon main-color" />
        </zm-tooltip>
      </div>
      <div class="opc-zcm-config__licenses-info">
        {{ $t('billing.zcm.max_license', [maxLicenses]) }}
      </div>
      <input-number-with-popover
        ref="userInput"
        size="large"
        controls-position="both"
        class="gap8"
        v-model="licenseNumber"
        :min="minLicenses"
        :max="maxLicenses"
        :label="`${title} select the number of licenses`"
        :increaseTips="increaseTips"
        :decreaseTips="decreaseTips"
        :dataTracking="{ buyInteraction: 'number of licenses', planName: planTrackingName }"
        @change="updatePlanLicense"
      />
      <alert v-if="showLicenseMaxAlert" type="info" class="gap16 opc-zcm-config__license-alert">
        <p>{{ $t('billing.zcm.license_count_tool_tip', [$t(currentBasePlan.planNameKey)]) }}</p>
        <a data-testid="checkout-a-92" href="/billing" @click="handleReturnToPlanMgt">{{ $t('billing.common.return_to_plan_management') }}</a>
      </alert>
      <alert v-if="mx_downSellLicenseErrorMessage" type="info" class="gap16 opc-zcm-config__license-alert">
        {{ mx_downSellLicenseErrorMessage }}
      </alert>
      <config-cycle
        v-model="billingCycle"
        :show-cycle-alert="!basePlanInSelected"
        :showMonthlyPriceFromCycle="true"
        :billing-cycle-list="billingCycleList"
        @switchCycleAction="updateBillingCycle"
      />
      <preview-payment class="gap24" />
    </div>
    <template #footer v-if="isShowRemoveBtn">
      <zm-button data-testid="checkout-button-93" type="link" ghost @click="removePlanByKey" :aria-label="`remove ${title} plan`">
        {{ $t('billing.common.remove') }}
      </zm-button>
    </template>
  </config-base>
</template>

<script>
import { get, chain, includes } from 'lodash';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import ConfigBase from '@/components/config-base.vue';
import ConfigCycle from '@/components/service/config-billing-cycle/index.vue';
import InputNumberWithPopover from '@/components/input-number/input-number-with-popover.vue';
import Alert from '@/components/zoom-ui-plus/alert/alert.vue';
import PreviewPayment from '@/views/checkout/payment/preview-payment.vue';
import { plansof } from '@/utils/chain';
import { translateToEn } from '@/plugins/i18n';
import plansMixin from '../mx/plan';
import addonMixin from '../mx/addon';
import planDetailsDialogMixin from './components/plan-details/plan-details-dialog';
import commonMixin from './mixin';
import baseplanFreeToPaidReplenishMixin, { FreeTrialAutoAdd } from "@/components/capacity/mx/freetrial-baseplan-replenish";
import { flowTypes, pageConfig } from '@/common/service/page-config';

const ConfigBaseComp = {
  name: 'ZCMBase',
  extends: ConfigBase,
  mixins: [commonMixin],
  data() {
    return {
      showLearnMore: false,
      showPlanDetails: true,
    };
  },
};

export default {
  name: 'ZCMConfig',
  mixins: [
    commonMixin,
    plansMixin,
    addonMixin,
    planDetailsDialogMixin,
    FreeTrialAutoAdd,
    pageConfig.type === flowTypes.add2cart ? baseplanFreeToPaidReplenishMixin : null,
  ].filter(Boolean),
  components: { ConfigBase: ConfigBaseComp, ConfigCycle, InputNumberWithPopover, Alert, PreviewPayment },
  data() {
    return {
      billingCycle: '',
      licenseNumber: 1,
    };
  },
  computed: {
    ...mapGetters([
      'getSelectedPlans',
      'getCurrentBasePlanInfo',
      'basePlanTotalLicenses',
      'getUserCurrentSubscriptions',
      'basicConfig',
      'currentEditPlan',
      'isProFreeTrialAddAddonCase'
    ]),

    selectedPlan() {
      return plansof(this.getSelectedPlans).type(this.planType).get(0);
    },

    currentBasePlan() {
      return get(this.getCurrentBasePlanInfo, ['plans', 0], {});
    },

    previousQuantity() {
      return this.currentEditPlan?.cartInfo?.selected_number ?? this.selectedPlan.min;
    },

    disableDownSell() {
      // Check account MRR > 500?
      return this.basicConfig.downSellData.disableDownSell;
    },

    minLicenses() {
      if (this.disableDownSell || !this.isStandardPrice) {
        return this.previousQuantity;
      }
      return this.selectedPlan.min;
    },

    maxLicenses() {
      if (this.isProFreeTrialAddAddonCase) {
        return this.selectedPlan.max
      }
      return Math.max(Math.min(this.basePlanTotalLicenses, this.selectedPlan.max), this.currentEditPlan?.cartInfo?.selected_number ?? 0);
    },

    /**
     * license number increase disabled tips
     * if base plan in selected plans show tips
     * else show 'Return to Plan Management' alert
     */
    increaseTips() {
      if (this.isProFreeTrialAddAddonCase) {
        return ''
      }
      if (this.basePlanInSelected && this.licenseNumber >= this.basePlanTotalLicenses) {
        return this.$t('billing.licences_limit_tips', [this.title, this.$t(this.currentBasePlan.planNameKey)]);
      }
      return '';
    },

    /**
     * license number decrease disabled tips
     */
    decreaseTips() {
      if (this.licenseNumber === this.previousQuantity) {
        if (this.disableDownSell) {
          return this.$t('billing.error.can_not_bought_plan_tips');
        }
        if (this.forNonStandardPriceTips) {
          return this.forNonStandardPriceTips;
        }
      }
      return '';
    },

    /**
     * Show 'Return to Plan Management' alert
     * if base plan in selected plans, no need to show
     */
    showLicenseMaxAlert() {
      if (!this.basePlanInSelected && this.licenseNumber >= this.basePlanTotalLicenses) {
        this.$tracking.buyInteraction(
          'banner - product level',
          `${this.planTrackingName} - ${translateToEn('billing.zcm.license_count_tool_tip', [
            translateToEn(this.currentBasePlan.planNameKey),
          ])} - impression`,
        );
        return true;
      }
      return false;
    },

    /**
     * implementing within plansMixin
     * disable billing cycle
     */
    currentPlanCycleDepFirstPlan() {
      const dependCyclePlanList = get(this.selectedPlan, 'billingCycleDependBasePlanTypeList', []);

      return chain(this.getUserCurrentSubscriptions)
        .filter((p) => includes(dependCyclePlanList, p.type))
        .orderBy((p) => p.cycleValue, 'desc')
        .get(0)
        .value();
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicense() {
      return (billingCycle) => {
        return (
          this.isEditFlow &&
          this.basicConfig?.isBulkLicenseAccount &&
          this.currentEditPlan?.billing_cycle !== billingCycle
        );
      };
    },

    /**
     * implementing within plansMixin
     */
    disabledForBulkLicenseTips() {
      return this.$t('billing.error.buld_acount_license');
    },

    /**
     * Is current base plan in selected plans
     */
    basePlanInSelected() {
      return this.getSelectedPlans.some((plan) => plan.type === this.currentBasePlan.type);
    },

    planTrackingName() {
      return translateToEn(this.selectedPlan?.planNameKey);
    },
  },
  watch: {
    maxLicenses(max) {
      if (this.licenseNumber > max) {
        this.licenseNumber = max;
      }
    }
  },
  methods: {
    ...mapMutations(['updateSelectPlan']),
    ...mapActions(['switchBillingCycleAction']),

    /**
     * update license number
     * @param {number} newLicense
     */
    updatePlanLicense(newLicense) {
      this.updateSelectPlan({
        plan_code: this.selectedPlan?.plan_code,
        cartInfo: { selected_number: newLicense },
      });
      this.mx_validDownSellLicense(newLicense);

      if (this.isProFreeTrialAddAddonCase) {
        this.mx_syncBasePlanLicenseForProFreeTrialAddonCase(newLicense);
      }
    },

    /**
     * update billing cycle
     * @param {object} oldPlan
     * @param {object} newPlan
     */
    async updateBillingCycle(oldPlan, newPlan) {
      this.$tracking.buyInteraction('subscription cycle', `${this.planTrackingName} - ${newPlan.billing_cycle}`);

      await this.switchBillingCycleAction([
        oldPlan?.plan_code,
        { ...newPlan, cartInfo: { selected_number: this.licenseNumber } },
      ]);
    },

    /**
     * click 'Return to Plan Management'
     * fire a event
     */
    handleReturnToPlanMgt() {
      this.$tracking.buyInteraction(
        'banner - product level',
        `${this.planTrackingName} - ${translateToEn('billing.zcm.license_count_tool_tip', [
          translateToEn(this.currentBasePlan.planNameKey),
        ])} - return to plan management`,
      );
    },
  },

  created() {
    if (this.selectedPlan) {
      this.billingCycle = this.selectedPlan.billing_cycle;
      this.licenseNumber = this.selectedPlan.cartInfo?.selected_number;

      // refresh page
      this.$nextTick(() => {
        this.mx_validDownSellLicense(this.licenseNumber);
      });
    }
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';
@import '@/styles/zoom-card.scss';

$sub-font-color: #666487;
$main-font-color: #00031f;

.main-color {
  color: $theme-color;
}

.gap8 {
  margin-top: 8px;
}
.gap16 {
  margin-top: 16px;
}
.gap24 {
  margin-top: 24px;
}

.opc-zcm-config {
  /deep/ .opc-zm-base-card {
    &__name {
      text-transform: none;
    }
    &__main--desc__content {
      font-size: 16px;
      color: $sub-font-color;
    }
  }

  &__item-label {
    font-size: 18px;
    font-weight: 700;
    color: $main-font-color;
    line-height: 27px;
  }

  &__info-icon {
    padding-left: 4px;
    font-size: 16px;
    line-height: 27px;
  }

  &__tips-content {
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    color: #747487;
  }

  &__licenses-info {
    color: $sub-font-color;
    font-size: 14px;
    line-height: 16.8px;
  }

  &__license-alert {
    padding: 16px;
    color: $sub-font-color;
    font-size: 14px;
    line-height: 21px;
    a {
      font-weight: 450;
    }
  }
}
</style>
